import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, Truck, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { PlankSymbol } from "./Logo";
import { useStore } from "../context/StoreContext";
import { inr, itemKey } from "../lib/api";

export const QtyStepper = ({ qty, onChange, testid, size = "sm" }) => (
  <div className={`inline-flex items-center rounded-full border border-jute/80 bg-card ${size === "lg" ? "h-12" : "h-9"}`}>
    <button data-testid={`${testid}-dec`} onClick={() => onChange(qty - 1)} className={`grid place-items-center rounded-full transition-colors hover:bg-walnut/5 ${size === "lg" ? "h-12 w-11" : "h-9 w-9"}`} aria-label="Decrease"><Minus className="h-3.5 w-3.5" /></button>
    <span data-testid={`${testid}-value`} className="w-6 text-center text-sm font-bold">{qty}</span>
    <button data-testid={`${testid}-inc`} onClick={() => onChange(qty + 1)} className={`grid place-items-center rounded-full transition-colors hover:bg-walnut/5 ${size === "lg" ? "h-12 w-11" : "h-9 w-9"}`} aria-label="Increase"><Plus className="h-3.5 w-3.5" /></button>
  </div>
);

const CartLine = ({ item }) => {
  const { updateQty, removeFromCart, setCartOpen } = useStore();
  const key = itemKey(item);
  const tid = key.replace(":", "-");
  return (
    <motion.li layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 40 }} transition={{ duration: 0.25 }} data-testid={`cart-item-${tid}`} className="flex gap-4 py-5">
      <div className="render h-24 w-24 shrink-0 rounded-xl"><img src={item.image} alt={item.name} className="h-full w-full object-cover" /></div>
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex justify-between gap-2">
          <div>
            <Link to={`/product/${item.slug}${item.variant ? `?finish=${item.variant}` : ""}`} onClick={() => setCartOpen(false)} className="font-display text-lg leading-tight hover:text-sheesham">{item.name}</Link>
            {item.variant_name && <p data-testid={`cart-variant-${tid}`} className="mt-0.5 text-xs text-muted-foreground">Finish: {item.variant_name}</p>}
          </div>
          <button data-testid={`cart-remove-${tid}`} onClick={() => removeFromCart(key)} aria-label="Remove" className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-sheesham/10 hover:text-sheesham"><Trash2 className="h-4 w-4" /></button>
        </div>
        <div className="flex items-center justify-between">
          <QtyStepper qty={item.qty} onChange={(q) => updateQty(key, q)} testid={`cart-qty-${tid}`} />
          <div className="text-right">
            <p className="price text-base">{inr(item.price * item.qty)}</p>
            {item.mrp > item.price && <p className="text-xs text-muted-foreground line-through">{inr(item.mrp * item.qty)}</p>}
            {item.gst_exclusive && <p className="text-xs text-muted-foreground">+ {item.gst_rate}% GST</p>}
          </div>
        </div>
      </div>
    </motion.li>
  );
};

export const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, subtotal, mrpTotal, count, taxBase } = useStore();
  const navigate = useNavigate();
  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent data-testid="cart-drawer" className="flex w-full flex-col bg-linen p-0 sm:max-w-md">
        <div className="flex items-center justify-between border-b border-jute/60 px-6 py-5">
          <SheetTitle className="font-display text-2xl font-normal tracking-tight">Your cart <span className="text-muted-foreground">({count})</span></SheetTitle>
        </div>
        {cart.length === 0 ? (
          <div data-testid="cart-empty" className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
            <span className="grid h-20 w-20 place-items-center rounded-full bg-sand"><PlankSymbol className="h-10 w-10 text-walnut" /></span>
            <p className="font-display text-2xl">Nothing in the box yet.</p>
            <p className="text-sm text-muted-foreground">Start with one rack. Add a module anytime.</p>
            <button data-testid="cart-empty-shop-button" onClick={() => { setCartOpen(false); navigate("/product/pustak-rack"); }} className="btn-sheesham mt-2">Shop the Pustak Rack</button>
          </div>
        ) : (
          <>
            <div className="mx-6 mt-4 flex items-center gap-3 rounded-xl bg-neem/10 px-4 py-3 text-sm text-neem"><Truck className="h-4 w-4 shrink-0" /><span><b>Free delivery unlocked.</b> Arrives within 24 hrs anywhere in Hyderabad.</span></div>
            <ul className="flex-1 divide-y divide-jute/50 overflow-y-auto px-6">
              <AnimatePresence initial={false}>{cart.map((i) => <CartLine key={itemKey(i)} item={i} />)}</AnimatePresence>
            </ul>
            <div className="space-y-3 border-t border-jute/60 bg-card px-6 py-5">
              {mrpTotal > subtotal && <div className="flex justify-between text-sm"><span className="text-muted-foreground">You save vs MRP</span><span data-testid="cart-savings" className="font-semibold text-neem">{inr(mrpTotal - subtotal)}</span></div>}
              <div className="flex items-baseline justify-between"><span className="text-base">Subtotal</span><span data-testid="cart-subtotal" className="price text-2xl">{inr(subtotal)}</span></div>
              <button data-testid="cart-checkout-button" onClick={() => { setCartOpen(false); navigate("/checkout"); }} className="btn-sheesham w-full py-4 text-base">Checkout</button>
              <p className="flex items-start gap-2 text-xs text-muted-foreground"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />{taxBase > 0 ? `GST of about ${inr(Math.round(taxBase))} is added at checkout for pieces priced excluding tax.` : "Prices include GST."} Coupons apply at checkout.</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
